import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Printer, Phone, Mail, AlertTriangle, PackageCheck, Truck, Pencil, CheckCircle2, XCircle, History, RotateCcw, Search, Clock, Plus } from "lucide-react";
import { fmtQty } from "@/lib/constants";
import { printPo } from "./poDoc";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import { DT } from "@/components/ui/denseTable";
import { formatINR } from "@/lib/money";
import { SHOW_COSTING } from "@/lib/features";
import { useT, useTn } from "@/lib/i18n";
import { loc } from "@/lib/numerals";
import { TranslatedName } from "@/components/TranslatedName";
import { SearchableSelect } from "@/components/ui/searchable-select";

// Server-side pagination is driven by pageSize state (10/50/100 selector in the footer).
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };
const shortName = (e) => (e || "").split("@")[0] || "—";
const STATUS = {
  "Pending Approval": "bg-purple-50 text-purple-700",
  Pending: "bg-blue-50 text-blue-700",
  Partial: "bg-amber-50 text-amber-700",
  Received: "bg-emerald-50 text-emerald-700",
  Cancelled: "bg-slate-100 text-slate-400 line-through",
  Rejected: "bg-rose-50 text-rose-700",
};

function EditDialog({ po, onClose, onEdit }) {
  const [qtys, setQtys] = useState(() => Object.fromEntries((po?.items || []).map((i) => [i.item_id, i.ordered_qty])));
  const [prices, setPrices] = useState(() => Object.fromEntries((po?.items || []).map((i) => [i.item_id, i.unit_price || ""])));
  if (!po) return null;
  const save = async () => {
    const items = po.items.map((i) => ({ item_id: i.item_id, sku: i.sku, name: i.name, unit: i.unit, ordered_qty: Math.max(1, Number(qtys[i.item_id]) || 0), unit_price: Number(prices[i.item_id]) || 0 }));
    await onEdit(po, items);
    onClose();
  };
  const grand = po.items.reduce((s, i) => s + (Number(qtys[i.item_id]) || 0) * (Number(prices[i.item_id]) || 0), 0);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-po-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit {po.po_number}</DialogTitle>
          <DialogDescription className="text-xs">Adjust ordered quantities and unit prices (before anything is received).</DialogDescription></DialogHeader>
        <div className="space-y-2 mt-2">{po.items.map((i, idx) => (
          <div key={`${i.item_id}-${idx}`} className="flex items-center gap-2 text-xs">
            <div className="flex-1 min-w-0"><p className="font-semibold text-slate-800 truncate">{i.name}</p><p className="text-slate-400 font-mono-data">{i.sku}</p></div>
            <Input type="number" min="1" className="h-8 w-20" placeholder="Qty" value={qtys[i.item_id]} onChange={(e) => setQtys((p) => ({ ...p, [i.item_id]: e.target.value }))} data-testid={`edit-qty-${i.sku}`} />
            {SHOW_COSTING && <Input type="number" min="0" step="0.01" className="h-8 w-24" placeholder="Unit ₹" value={prices[i.item_id]} onChange={(e) => setPrices((p) => ({ ...p, [i.item_id]: e.target.value }))} data-testid={`edit-price-${i.sku}`} />}
            {SHOW_COSTING && <span className="w-24 text-right font-mono-data text-slate-600" data-testid={`edit-line-total-${i.sku}`}>{formatINR((Number(qtys[i.item_id]) || 0) * (Number(prices[i.item_id]) || 0))}</span>}
          </div>))}</div>
        <DialogFooter className="mt-4 sm:justify-between">
          {SHOW_COSTING && <span className="mr-auto text-xs text-slate-600">PO Total: <span className="font-mono-data font-semibold text-slate-900" data-testid="edit-po-total">{formatINR(grand)}</span></span>}
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="edit-po-save">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplierEditDialog({ sup, onClose, onSave }) {
  const [phone, setPhone] = useState(sup?.phone || "");
  const [email, setEmail] = useState(sup?.email || "");
  if (!sup) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="supplier-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{sup.name}</DialogTitle>
          <DialogDescription className="text-xs">Reliability {sup.reliability_score}% · {sup.on_time_count || 0} on-time / {sup.late_count || 0} late</DialogDescription></DialogHeader>
        <div className="space-y-2 mt-2">
          <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="supplier-phone-input" />
          <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="supplier-email-input" />
          {sup.late_deliveries?.length > 0 && (
            <div className="text-[11px] text-slate-500 pt-1"><p className="font-semibold mb-1">Late delivery history</p>
              {sup.late_deliveries.map((l, idx) => <p key={`${l.po_number || "late"}-${l.received_at || idx}`} className="font-mono-data">{l.po_number}{l.days_late != null ? ` · ${l.days_late}d late` : ""}</p>)}</div>
          )}
        </div>
        <DialogFooter className="mt-4 sm:justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" onClick={async () => { await onSave(sup, { phone, email }); onClose(); }} data-testid="supplier-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ po, onClose, onReceive }) {
  const [qtys, setQtys] = useState({});
  if (!po) return null;
  const submit = async (mode) => {
    if (mode === "partial") {
      const items = po.items
        .map((i) => ({ item_id: i.item_id, qty: Math.min(Number(qtys[i.item_id]) || 0, i.ordered_qty - (i.received_qty || 0)) }))
        .filter((x) => x.qty > 0);
      if (items.length === 0) {
        toast.error("Enter a quantity (within the outstanding balance) to record a partial delivery");
        return;
      }
      await onReceive(po, "partial", items);
    } else {
      await onReceive(po, "full");
    }
    onClose();
  };
  return (
    <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="receive-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Truck className="h-4 w-4 text-blue-600" /> Receive {po.po_number}</DialogTitle>
          <DialogDescription className="text-xs">Mark the whole PO received, or enter partial amounts to keep it open for the balance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {po.items.map((i, idx) => {
            const bal = i.ordered_qty - (i.received_qty || 0);
            return (
              <div key={`${i.item_id}-${idx}`} className="flex items-center gap-2 text-xs">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{i.name}</p>
                  <p className="text-slate-400 font-mono-data">{i.sku} · balance {fmtQty(bal, i.unit)} {i.unit}</p>
                </div>
                <Input type="number" min="0" max={bal} placeholder="qty" className="h-8 w-24"
                  value={qtys[i.item_id] ?? ""} onChange={(e) => setQtys((p) => ({ ...p, [i.item_id]: e.target.value }))}
                  data-testid={`receive-qty-${i.sku}`} />
              </div>
            );
          })}
        </div>
        <DialogFooter className="mt-4 sm:justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => submit("partial")} data-testid="record-partial-btn">Record Partial Delivery</Button>
          <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => submit("full")} data-testid="mark-received-btn">Mark as Fully Received</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDialog({ po, onClose }) {
  if (!po) return null;
  return (
    <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="contact-supplier-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Contact {po.supplier || "Supplier"}</DialogTitle>
          <DialogDescription className="text-xs">Chase the delivery for {po.po_number}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2 text-sm">
          <a href={`tel:${po.supplier_phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600" data-testid="contact-phone">
            <Phone className="h-4 w-4" /> {po.supplier_phone || "No phone on file"}
          </a>
          <a href={`mailto:${po.supplier_email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600" data-testid="contact-email">
            <Mail className="h-4 w-4" /> {po.supplier_email || "No email on file"}
          </a>
        </div>
        <DialogFooter className="mt-4">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ po, kind, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [po]);
  if (!po) return null;
  const isReject = kind === "reject";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${kind}-po-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{isReject ? "Reject" : "Approve"} {po.po_number}</DialogTitle>
          <DialogDescription className="text-xs">
            {isReject
              ? "Send this PO back to the planner. Add a reason so they know why."
              : "Approve this PO to make it active. You can add an optional note for the planner."}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder={isReject ? "Reason for rejection (recommended)" : "Optional note for the planner"}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 text-sm"
          rows={3}
          data-testid={`${kind}-note-input`}
        />
        <DialogFooter className="mt-4 sm:justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            className={`h-8 text-xs ${isReject ? "bg-rose-600 hover:bg-rose-700" : "bg-purple-600 hover:bg-purple-700"}`}
            onClick={async () => { await onConfirm(po, note.trim()); onClose(); }}
            data-testid={`${kind}-confirm-btn`}
          >
            {isReject ? "Reject PO" : "Approve PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ po, onClose }) {
  if (!po) return null;
  const events = po.events || [];
  const label = { raised: "Raised", approved: "Approved", rejected: "Rejected", "partial received": "Partial delivery", received: "Received", cancelled: "Cancelled", edited: "Edited" };
  const dot = { raised: "bg-slate-400", approved: "bg-emerald-500", rejected: "bg-rose-500", "partial received": "bg-amber-500", received: "bg-emerald-600", cancelled: "bg-slate-400", edited: "bg-blue-400" };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="history-po-dialog">
        <DialogHeader><DialogTitle className="font-display">{po.po_number} — Timeline</DialogTitle>
          <DialogDescription className="text-xs">Full approval & delivery history for this purchase order.</DialogDescription></DialogHeader>
        <ol className="mt-2 space-y-3">
          {events.length === 0 && <li className="text-xs text-slate-400">No history recorded.</li>}
          {events.map((e, i) => (
            <li key={`${e.at}-${e.type}-${i}`} className="flex gap-3 text-xs" data-testid={`history-event-${i}`}>
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${dot[e.type] || "bg-slate-300"}`} />
              <div className="min-w-0">
                <p className="font-semibold text-slate-800">{label[e.type] || e.type} <span className="font-normal text-slate-400">· {shortName(e.actor)}</span></p>
                <p className="text-slate-400 font-mono-data">{new Date(e.at).toLocaleString("en-IN")}</p>
                {e.note && <p className="text-slate-500 italic">“{e.note}”</p>}
              </div>
            </li>
          ))}
        </ol>
        <DialogFooter className="mt-4">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResubmitDialog({ po, onClose, onResubmit }) {
  const [qtys, setQtys] = useState(() => Object.fromEntries((po?.items || []).map((i) => [i.item_id, i.ordered_qty])));
  const [prices, setPrices] = useState(() => Object.fromEntries((po?.items || []).map((i) => [i.item_id, i.unit_price || ""])));
  if (!po) return null;
  const save = async () => {
    const items = po.items.map((i) => ({ item_id: i.item_id, sku: i.sku, name: i.name, unit: i.unit, ordered_qty: Math.max(1, Number(qtys[i.item_id]) || 0), unit_price: Number(prices[i.item_id]) || 0 }));
    await onResubmit(po, items);
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="resubmit-po-dialog">
        <DialogHeader><DialogTitle className="font-display">Resubmit {po.po_number}</DialogTitle>
          <DialogDescription className="text-xs">Tweak quantities and prices if needed and raise a fresh PO for approval.{po.rejection_note ? ` Rejected because: “${po.rejection_note}”` : ""}</DialogDescription></DialogHeader>
        <div className="space-y-2 mt-2">{po.items.map((i, idx) => (
          <div key={`${i.item_id}-${idx}`} className="flex items-center gap-2 text-xs">
            <div className="flex-1 min-w-0"><p className="font-semibold text-slate-800 truncate">{i.name}</p><p className="text-slate-400 font-mono-data">{i.sku}</p></div>
            <Input type="number" min="1" className="h-8 w-20" placeholder="Qty" value={qtys[i.item_id]} onChange={(e) => setQtys((p) => ({ ...p, [i.item_id]: e.target.value }))} data-testid={`resubmit-qty-${i.sku}`} />
            {SHOW_COSTING && <Input type="number" min="0" step="0.01" className="h-8 w-24" placeholder="Unit ₹" value={prices[i.item_id]} onChange={(e) => setPrices((p) => ({ ...p, [i.item_id]: e.target.value }))} data-testid={`resubmit-price-${i.sku}`} />}
          </div>))}</div>
        <DialogFooter className="mt-4 sm:justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-8 text-xs bg-purple-600 hover:bg-purple-700" onClick={save} data-testid="resubmit-confirm-btn">Resubmit for Approval</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatePoDialog({ onClose, suppliers, bomItems, isAdmin, onSubmit }) {
  const [supplier, setSupplier] = useState("");
  const [lead, setLead] = useState("0");
  const [pick, setPick] = useState("");
  const [lines, setLines] = useState([]); // [{ item_id, sku, name, unit, qty }]
  const [saving, setSaving] = useState(false);
  const tr = useT();
  const purchasable = useMemo(
    () => (bomItems || []).filter((b) => ["raw", "spare"].includes(b.item_type) && b.status !== "archived"),
    [bomItems],
  );
  const available = purchasable.filter((b) => !lines.some((l) => l.item_id === b.id));
  const pickSupplier = (name) => {
    setSupplier(name);
    const s = (suppliers || []).find((x) => x.name === name);
    if (s) setLead(String(s.default_lead_time_days || 0));
  };
  const addLine = () => {
    const it = purchasable.find((b) => b.id === pick);
    if (!it) return;
    setLines((ls) => [...ls, { item_id: it.id, sku: it.sku, name: it.name, unit: it.unit || "pcs", qty: "" }]);
    setPick("");
  };
  const validLines = lines.filter((l) => Number(l.qty) > 0);
  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(supplier, validLines.map((l) => ({ item_id: l.item_id, sku: l.sku, name: l.name, unit: l.unit, ordered_qty: Number(l.qty), unit_price: 0 })), Number(lead) || 0);
      onClose();
    } catch { /* error toast already shown by the action; keep the dialog open */ } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="create-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tr("po.newTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{tr("po.newDesc")} {isAdmin ? tr("po.newDescAdmin") : tr("po.newDescApproval")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2 mt-2">
          <div className="flex-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tr("col.supplier")}</label>
            <SearchableSelect
              value={supplier}
              onValueChange={pickSupplier}
              options={(suppliers || []).map((s) => ({ value: s.name, label: s.name, keywords: s.name }))}
              placeholder={tr("po.selectSupplier")}
              searchPlaceholder={tr("po.selectSupplier")}
              emptyText={tr("po.empty")}
              className="mt-1"
              triggerClassName="h-8 text-xs"
              testid="po-supplier-select"
            />
          </div>
          <div className="w-28">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tr("po.leadTime")}</label>
            <Input type="number" min="0" className="mt-1 h-8" value={lead} onChange={(e) => setLead(e.target.value)} data-testid="po-lead-time" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <SearchableSelect
            value={pick}
            onValueChange={setPick}
            options={available.map((b) => ({ value: b.id, label: `${b.name} (${b.sku})`, keywords: `${b.name} ${b.sku}` }))}
            placeholder={tr("po.pickItem")}
            searchPlaceholder={tr("po.pickItem")}
            emptyText={tr("po.empty")}
            className="flex-1"
            triggerClassName="h-8 text-xs"
            testid="po-item-select"
          />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addLine} disabled={!pick} data-testid="po-add-item-btn">
            <Plus className="h-3 w-3 mr-1" /> {tr("po.addItem")}
          </Button>
        </div>
        {lines.length > 0 && (
          <div className="space-y-2 mt-3 max-h-56 overflow-y-auto" data-testid="create-po-lines">
            {lines.map((l) => (
              <div key={l.item_id} className="flex items-center gap-2 text-xs">
                <div className="flex-1 min-w-0"><p className="font-semibold text-slate-800 truncate">{l.name}</p><p className="text-slate-400 font-mono-data">{l.sku} · {l.unit}</p></div>
                <Input type="number" min="1" className="h-8 w-24" placeholder={tr("col.qty")} value={l.qty} onChange={(e) => setLines((ls) => ls.map((x) => (x.item_id === l.item_id ? { ...x, qty: e.target.value } : x)))} data-testid={`po-qty-${l.sku}`} />
                <button onClick={() => setLines((ls) => ls.filter((x) => x.item_id !== l.item_id))} className="text-slate-400 hover:text-rose-600" data-testid={`po-remove-${l.sku}`} aria-label={`Remove ${l.sku}`}>
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="mt-4 sm:justify-between">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>{tr("common.cancel")}</Button>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" disabled={!supplier || validLines.length === 0 || saving} onClick={submit} data-testid="create-po-submit">
            {tr("po.raise")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PurchaseOrdersPanel({ suppliers, canManage, isAdmin, mineOnly, currentEmail, facility, poVersion, bomItems = [], onCreatePO, onReceive, onEdit, onCancel, onApprove, onReject, onResubmit, onBulkApprove, onUpdateSupplier, onImported }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [receivePo, setReceivePo] = useState(null);
  const [contactPo, setContactPo] = useState(null);
  const [editPo, setEditPo] = useState(null);
  const [supEdit, setSupEdit] = useState(null);
  const [approvePo, setApprovePo] = useState(null);
  const [rejectPo, setRejectPo] = useState(null);
  const [historyPo, setHistoryPo] = useState(null);
  const [resubmitPo, setResubmitPo] = useState(null);
  const [search, setSearch] = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [sort, setSort] = useState({ key: "status", dir: "asc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // rule R5: items-per-page selector (10/50/100)
  const [selected, setSelected] = useState({});
  const [data, setData] = useState({ items: [], total: 0, page_count: 1, active_count: 0, total_all: 0, pending_approval_ids: [] });
  const [loading, setLoading] = useState(false);
  const tr = useT();
  const tn = useTn();

  useEffect(() => {
    const t = setTimeout(() => { setDebSearch(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Single memoised query string keeps the fetch effect down to two concerns: "what" (query) and "when" (poVersion).
  const query = useMemo(
    () => `/purchase-orders?facility=${encodeURIComponent(facility)}&status_filter=all${mineOnly ? "&mine=true" : ""}${debSearch ? `&q=${encodeURIComponent(debSearch)}` : ""}&sort=${sort.key}&dir=${sort.dir}&page=${page}&page_size=${pageSize}`,
    [facility, mineOnly, debSearch, sort, page, pageSize],
  );

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(query);
        if (!ignore) setData(res);
      } catch (e) {
        logger.warn("Failed to load purchase orders", e); // realtime/poVersion will retry
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [query, poVersion, setData, setLoading]);

  useEffect(() => { if (page > 0 && page >= data.page_count) setPage(data.page_count - 1); }, [data.page_count, page]);

  const pos = data.items || [];
  const pendingApprovalIds = data.pending_approval_ids || [];
  const selectedIds = pendingApprovalIds.filter((id) => selected[id]);
  const showSelectCol = isAdmin && pendingApprovalIds.length > 0;

  const toggleSort = (key) => { setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" })); setPage(0); };
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelectAll = () => {
    if (pendingApprovalIds.length > 0 && pendingApprovalIds.every((id) => selected[id])) setSelected({});
    else setSelected(Object.fromEntries(pendingApprovalIds.map((id) => [id, true])));
  };
  const doBulkApprove = async () => { await onBulkApprove(selectedIds); setSelected({}); };

  return (
    <div className="space-y-6" data-testid="purchase-orders-panel">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-blue-600" />
          <h3 className="font-display font-semibold text-slate-800">{mineOnly ? tr("po.titleMine") : tr("po.titleAll")}</h3>
          <span className="text-xs text-slate-400" data-testid="po-count">{loc(String(data.active_count))} {tr("common.active")} · {loc(String(data.total_all))} {tr("common.total")}</span>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr("common.searchPo")} className="h-7 w-52 pl-7 text-[11px]" data-testid="po-search-input" />
            </div>
            {onCreatePO && (
              <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)} data-testid="new-po-btn">
                <Plus className="h-3 w-3 mr-1" /> {tr("po.new")}
              </Button>
            )}
            {canManage && <ImportExportButtons entity="purchase_orders" label="purchase orders" facility={facility} allowUpsert={false} onImported={onImported} />}
            {isAdmin && selectedIds.length > 0 && (
              <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={doBulkApprove} data-testid="bulk-approve-btn">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {tr("common.approve")} {loc(String(selectedIds.length))}
              </Button>
            )}
          </div>
        </div>
        {pos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="po-empty">
            <PackageCheck className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">{loading ? "Loading…" : tr("po.empty")}</p>
          </div>
        ) : (
          <>
          <div className={DT.wrap}>
            <table className={`${DT.table} min-w-[900px]`}>
              <thead className={DT.thead}>
                <tr>
                  {showSelectCol && (
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" checked={pendingApprovalIds.every((id) => selected[id])} onChange={toggleSelectAll} data-testid="po-select-all" aria-label="Select all pending approval" />
                    </th>
                  )}
                  <th className="text-left font-semibold px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("po_number")} data-testid="po-sort-po">{tr("col.po")}{sortArrow("po_number")}</th>
                  <th className="text-left font-semibold px-3 py-2">{tr("col.supplier")}</th>
                  <th className="text-left font-semibold px-3 py-2">{tr("common.items")}</th>
                  <th className="text-left font-semibold px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="po-sort-status">{tr("common.status")}{sortArrow("status")}</th>
                  <th className="text-left font-semibold px-3 py-2">{tr("col.audit")}</th>
                  <th className="text-left font-semibold px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("expected")} data-testid="po-sort-expected">{tr("col.expected")}{sortArrow("expected")}</th>
                  {SHOW_COSTING && <th className="text-right font-semibold px-3 py-2">{tr("col.total")}</th>}
                  <th className="text-right font-semibold px-3 py-2">{tr("common.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pos.map((po) => (
                  <tr key={po.id} className={`border-b border-slate-100 transition-colors duration-150 ${po.status === "Pending Approval" ? "bg-yellow-50/70 hover:bg-yellow-100/80" : po.overdue ? "bg-red-50/50 hover:bg-red-100/70" : "even:bg-slate-50/60 hover:bg-slate-100/80"}`} data-testid={`po-row-${po.po_number}`}>
                    {showSelectCol && (
                      <td className="px-3 py-2">
                        {po.status === "Pending Approval" && (
                          <input type="checkbox" checked={!!selected[po.id]} onChange={() => toggleSelect(po.id)} data-testid={`po-select-${po.po_number}`} aria-label={`Select ${po.po_number}`} />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono-data font-semibold text-slate-800">{po.po_number}</td>
                    <td className="px-3 py-2 text-slate-600">{po.supplier ? <TranslatedName text={po.supplier} /> : "—"}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {po.items.map((i) => `${tn(i.name)} (${fmtQty(i.received_qty, i.unit)}/${fmtQty(i.ordered_qty, i.unit)} ${i.unit})`).join(", ")}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded font-mono-data text-[10px] ${STATUS[po.status] || "bg-slate-100 text-slate-600"}`} data-testid={`po-status-${po.po_number}`}>{po.status}</span>
                      {po.sla_breached && (
                        <span className="ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold align-middle" title={`Awaiting approval ${po.awaiting_hours}h`} data-testid={`sla-flag-${po.po_number}`}>
                          <Clock className="h-2.5 w-2.5" /> SLA
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-slate-500 whitespace-nowrap max-w-[180px]" data-testid={`po-audit-${po.po_number}`}>
                      <div>raised by {shortName(po.created_by)}</div>
                      {po.approved_by && <div className="text-emerald-600">approved by {shortName(po.approved_by)}</div>}
                      {po.approval_note && <div className="text-slate-500 italic truncate" title={po.approval_note}>“{po.approval_note}”</div>}
                      {po.rejected_by && <div className="text-rose-600">rejected by {shortName(po.rejected_by)}</div>}
                      {po.rejection_note && <div className="text-rose-500 italic truncate" title={po.rejection_note}>“{po.rejection_note}”</div>}
                    </td>
                    <td className={`px-3 py-2 font-mono-data whitespace-nowrap ${po.overdue ? "text-red-600 font-bold" : "text-slate-600"}`}>
                      <span className="inline-flex items-center gap-1">
                        {po.overdue && <AlertTriangle className="h-3 w-3" data-testid={`po-overdue-${po.po_number}`} />}
                        {fmt(po.expected_date)}
                      </span>
                    </td>
                    {SHOW_COSTING && <td className="px-3 py-2 text-right font-mono-data text-slate-700" data-testid={`po-amount-${po.po_number}`}>{po.total_amount > 0 ? formatINR(po.total_amount) : "—"}</td>}
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {po.overdue && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-200 text-red-600" onClick={() => setContactPo(po)} data-testid={`contact-supplier-${po.po_number}`}>
                            <Phone className="h-3 w-3 mr-1" /> {tr("common.contact")}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setHistoryPo(po)} data-testid={`history-po-${po.po_number}`}>
                          <History className="h-3 w-3 mr-1" /> {tr("common.history")}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => printPo(po)} data-testid={`download-po-${po.po_number}`}>
                          <Printer className="h-3 w-3 mr-1" /> PDF
                        </Button>
                        {canManage && po.status === "Rejected" && po.created_by === currentEmail && (
                          <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => setResubmitPo(po)} data-testid={`resubmit-po-${po.po_number}`}>
                            <RotateCcw className="h-3 w-3 mr-1" /> {tr("common.resubmit")}
                          </Button>
                        )}
                        {isAdmin && po.status === "Pending Approval" && (
                          <>
                            <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => setApprovePo(po)} data-testid={`approve-po-${po.po_number}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> {tr("common.approve")}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200" onClick={() => setRejectPo(po)} data-testid={`reject-po-${po.po_number}`}>
                              <XCircle className="h-3 w-3 mr-1" /> {tr("common.reject")}
                            </Button>
                          </>
                        )}
                        {isAdmin && (po.status === "Pending" || po.status === "Pending Approval") && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setEditPo(po)} data-testid={`edit-po-${po.po_number}`}>{tr("common.edit")}</Button>
                        )}
                        {isAdmin && (po.status === "Pending" || po.status === "Partial") && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200" onClick={() => onCancel(po)} data-testid={`cancel-po-${po.po_number}`}>{tr("common.cancel")}</Button>
                        )}
                        {isAdmin && (po.status === "Pending" || po.status === "Partial") && (
                          <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => setReceivePo(po)} data-testid={`receive-po-${po.po_number}`}>
                            <Truck className="h-3 w-3 mr-1" /> {tr("common.receive")}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {SHOW_COSTING && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td colSpan={showSelectCol ? 7 : 6} className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">Page total</td>
                    <td className="px-3 py-2 text-right font-mono-data font-bold text-slate-900" data-testid="po-page-total">{formatINR(pos.reduce((s, po) => s + (["Cancelled", "Rejected"].includes(po.status) ? 0 : (po.total_amount || 0)), 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-t border-slate-100 text-xs text-slate-500" data-testid="po-pagination">
            <div className="inline-flex items-center gap-2">
              <span>{tr("common.itemsPerPage") !== "common.itemsPerPage" ? tr("common.itemsPerPage") : "Items per page"}</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} className="h-7 text-xs border border-slate-200 rounded-md px-1.5 bg-white" data-testid="po-per-page">
                {[10, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span>{tr("common.page")} {loc(String(page + 1))} {tr("common.of")} {loc(String(data.page_count))} · {loc(String(data.total))} {tr("common.total")}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="po-page-prev">Prev</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page >= data.page_count - 1} onClick={() => setPage(page + 1)} data-testid="po-page-next">Next</Button>
            </div>
          </div>
          </>
        )}
      </div>

      {suppliers?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="font-display font-semibold text-slate-800 text-sm mb-3">{tr("suppliers.reliability")}</h3>
          <div className={DT.wrap} data-testid="supplier-reliability-table">
            <table className={`${DT.table} min-w-[640px]`}>
              <thead className={DT.thead}>
                <tr className={DT.headerRow}>
                  <th className={DT.th}>{tr("col.supplier")}</th>
                  <th className={DT.thRight}>{tr("col.reliability")}</th>
                  <th className={DT.th}>Deliveries</th>
                  <th className={DT.th}>{tr("common.contact")}</th>
                  <th className={`${DT.thRight} ${DT.stickyRight}`}>{tr("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.id} className={DT.row} data-testid={`supplier-${s.id}`}>
                    <td className={DT.td}><p className="font-semibold text-slate-800"><TranslatedName text={s.name} /></p></td>
                    <td className={DT.tdRight}>
                      <span className={`font-mono-data text-xs font-bold ${s.reliability_score >= 90 ? "text-emerald-600" : s.reliability_score >= 70 ? "text-amber-600" : "text-red-600"}`} data-testid={`reliability-${s.id}`}>
                        {s.reliability_score}%
                      </span>
                    </td>
                    <td className={`${DT.td} text-slate-500`}>
                      {(s.on_time_count || 0)} on-time · {(s.late_count || 0)} late
                      {s.late_deliveries?.length ? ` · last late ${fmt(s.late_deliveries[s.late_deliveries.length - 1].received_at)}` : ""}
                    </td>
                    <td className={`${DT.td} text-slate-500`}>
                      <span className="inline-flex items-center gap-1 mr-3"><Phone className="h-3 w-3" />{s.phone || "—"}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email || "—"}</span>
                    </td>
                    <td className={`${DT.td} ${DT.stickyRight}`}>
                      <div className={DT.actionsCell}>
                        {isAdmin && (
                          <button onClick={() => setSupEdit(s)} className={`${DT.iconBtn} hover:text-blue-600`} data-testid={`supplier-edit-${s.id}`} title="Edit contact">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && <CreatePoDialog onClose={() => setCreateOpen(false)} suppliers={(suppliers || []).filter((s) => s.active !== false)} bomItems={bomItems} isAdmin={isAdmin} onSubmit={onCreatePO} />}
      <ReceiveDialog po={receivePo} onClose={() => setReceivePo(null)} onReceive={onReceive} />
      <ContactDialog po={contactPo} onClose={() => setContactPo(null)} />
      {editPo && <EditDialog po={editPo} onClose={() => setEditPo(null)} onEdit={onEdit} />}
      {supEdit && <SupplierEditDialog sup={supEdit} onClose={() => setSupEdit(null)} onSave={onUpdateSupplier} />}
      {approvePo && <DecisionDialog po={approvePo} kind="approve" onClose={() => setApprovePo(null)} onConfirm={onApprove} />}
      {rejectPo && <DecisionDialog po={rejectPo} kind="reject" onClose={() => setRejectPo(null)} onConfirm={onReject} />}
      {historyPo && <HistoryDialog po={historyPo} onClose={() => setHistoryPo(null)} />}
      {resubmitPo && <ResubmitDialog po={resubmitPo} onClose={() => setResubmitPo(null)} onResubmit={onResubmit} />}
    </div>
  );
}
